import type { TimelineItem, TimelineTrack, AudioItem } from "../types/timeline";
import { getProxyUrl, isPublicR2Url, unwrapProxyUrl } from "./url-utils";
import { buildClipMotionEntry } from "./clip-motion";

/** 프로젝트 로드 시 세팅되는 TTS 재생성 메타 - audio-section에서 읽음 */
export const cenemaProjectMeta: { voiceId: string; ttsSpeed: number } = {
    voiceId: 'Aoede',
    ttsSpeed: 1.2,
};

/**
 * Refined Mapper for Cenema AppState (Scene-based)
 */

export const mapCenemaToSuperEdit = (
    cenemaData: any, 
    fps: number = 30, 
    isVerticalLayout: boolean = false,
    existingItems: TimelineItem[] = []
): { items: TimelineItem[], tracks: TimelineTrack[], assets: any[], keyframes?: any[] } => {
    console.group('cenema-mapper: Deep Mapping');
    const MIN_CAPTION_DISPLAY_FRAMES = Math.max(1, Math.round(fps * 0.55));
    const CAPTION_GAP_FILL_FRAMES = Math.max(1, Math.round(fps * 0.18));
    const DEFAULT_CAPTION_FONT_SIZE = 72;
    let isVertical = false;
    const toFiniteNumber = (value: unknown): number | null => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const getSceneSpeechWindow = (scene: any) => {
        if (!isVertical) {
            return null;
        }

        const explicitStartMs = toFiniteNumber(scene?.speech_start_ms ?? scene?.speechStartMs ?? scene?.audioStartMs);
        const explicitEndMs = toFiniteNumber(scene?.speech_end_ms ?? scene?.speechEndMs ?? scene?.audioEndMs);
        if (explicitStartMs !== null && explicitEndMs !== null && explicitEndMs > explicitStartMs) {
            return {
                startSec: Math.max(0, explicitStartMs / 1000),
                endSec: Math.max(0, explicitEndMs / 1000),
            };
        }

        const captionSegments = Array.isArray(scene?.caption_segments)
            ? scene.caption_segments
            : Array.isArray(scene?.captionSegments)
                ? scene.captionSegments.map((captionSegment: any) => ({
                    text: captionSegment?.text,
                    start_ms: captionSegment?.startMs,
                    end_ms: captionSegment?.endMs,
                }))
                : [];

        if (captionSegments.length > 0) {
            const first = captionSegments[0];
            const last = captionSegments[captionSegments.length - 1];
            const startMs = toFiniteNumber(first?.start_ms);
            const endMs = toFiniteNumber(last?.end_ms);
            if (startMs !== null && endMs !== null && endMs > startMs) {
                return {
                    startSec: Math.max(0, startMs / 1000),
                    endSec: Math.max(0, endMs / 1000),
                };
            }
        }

        const wordTimestamps = Array.isArray(scene?.word_timestamps)
            ? scene.word_timestamps
            : Array.isArray(scene?.speechToText?.words)
                ? scene.speechToText.words
                : [];

        if (wordTimestamps.length > 0) {
            const first = wordTimestamps[0];
            const last = wordTimestamps[wordTimestamps.length - 1];
            const start = toFiniteNumber(first?.start ?? first?.from);
            const end = toFiniteNumber(last?.end ?? last?.to);
            if (start !== null && end !== null && end > start) {
                return {
                    startSec: Math.max(0, start),
                    endSec: Math.max(0, end),
                };
            }
        }

        return null;
    };

    const normalizeCaptionTrackItems = (
        captionItems: TimelineItem[],
        sceneWindows: Map<string, { from: number; to: number }>
    ) => {
        const grouped = new Map<string, TimelineItem[]>();

        captionItems.forEach((item) => {
            const sceneId = String((item as any).sceneId || `caption-${item.id}`);
            if (!grouped.has(sceneId)) grouped.set(sceneId, []);
            grouped.get(sceneId)!.push({ ...item });
        });

        const normalized: TimelineItem[] = [];

        grouped.forEach((groupItems, sceneId) => {
            const sorted = [...groupItems].sort((a, b) => a.from - b.from);
            const sceneWindow = sceneWindows.get(sceneId);
            const sceneEnd = sceneWindow?.to ?? Number.POSITIVE_INFINITY;

            for (let i = 0; i < sorted.length; i += 1) {
                const current = sorted[i];
                current.durationInFrames = Math.max(1, Math.round(current.durationInFrames || 1));

                const next = sorted[i + 1];
                const nextStart = next
                    ? Math.max(current.from + 1, next.from)
                    : sceneEnd;
                const clampedEnd = Math.max(
                    current.from + 1,
                    Math.min(nextStart, sceneEnd)
                );

                current.durationInFrames = Math.max(1, clampedEnd - current.from);
            }

            normalized.push(...sorted);
        });

        return normalized.sort((a, b) => a.from - b.from);
    };

    const getSceneDurationSec = (scene: any) => {
        const speechWindow = getSceneSpeechWindow(scene);
        const speechDuration = speechWindow && speechWindow.endSec > speechWindow.startSec
            ? (speechWindow.endSec - speechWindow.startSec)
            : 0;

        const audioDuration = Number(scene?.audio_duration);
        if (Number.isFinite(audioDuration) && audioDuration > 0) {
            return audioDuration;
        }

        const explicitDuration = Number(scene?.duration);
        const timelineDuration = Number(scene?.duration_sec);
        const targetSceneDuration = Number(scene?.targetSceneDurationSec ?? scene?.target_scene_duration_sec);
        const estimatedFromText = (() => {
            const text = String(scene?.description || scene?.text || '').trim();
            if (!text) return 0;
            return Math.max(1.2, Number(((text.replace(/\s+/g, '').length / 14) * 1.8).toFixed(2)));
        })();
        return Math.max(
            Number.isFinite(explicitDuration) && explicitDuration > 0 ? explicitDuration : 0,
            Number.isFinite(timelineDuration) && timelineDuration > 0 ? timelineDuration : 0,
            speechDuration,
            Number.isFinite(targetSceneDuration) && targetSceneDuration > 0 ? targetSceneDuration : 0,
            estimatedFromText,
            2
        );
    };

    const containsDenseScript = (text: string) => /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Hangul}]/u.test(text);
    const requiresNoSpaceJoin = (text: string) => /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}]/u.test(text);
    const isBreakPunctuation = (text: string) => /[,.!?;:…、。！？；：]/u.test(text);
    const isKoreanSentenceEnd = (text: string) => /(니다|습니다|에요|예요|거든요|잖아요|는데요|세요|까요|죠|어요|아요|네요|구요|대요|래요|다고|라고|냐고|답니다)$/.test(text.trim());
    const startsWithKoreanConnector = (text: string) => /^(그리고|그러나|그래서|하지만|그렇지만|또한|그런데|따라서|즉|결국|마침내|드디어|그러면|그러므로|반면|오히려|그래도|물론|심지어|게다가|더구나|더욱이|한편|또|또는|혹은|다만|반대로)/.test(text.trim());
    const endsWithWeakKoreanBoundary = (text: string) =>
        /(은|는|이|가|을|를|에|의|도|로|으로|에서|까지|부터|에게|한테|와|과|하고|처럼|만큼|보다|같이|대로|밖에|마저|조차|라도|이라도|이나|나|및|또는|혹은)$/.test(text.trim());
    const endsWithKoreanConnector = (text: string) =>
        /(고|며|면서|지만|는데|니까|므로|아서|어서|해서|라서|다가|자마자|려고|도록|게|면|며서|하며|되고|하고|되어|돼서)$/.test(text.trim());
    const getKoreanBoundaryScore = (lastToken: string, nextToken: string | null): number => {
        const token = String(lastToken || '').trim();
        const next = String(nextToken || '').trim();
        if (!token) return 0;

        let score = 0;

        if (isBreakPunctuation(token)) score += 18;
        if (isKoreanSentenceEnd(token)) score += 16;
        if (startsWithKoreanConnector(next)) score += 10;

        if (endsWithWeakKoreanBoundary(token)) score -= 14;
        if (endsWithKoreanConnector(token)) score -= 10;
        if (!next && !isKoreanSentenceEnd(token) && !isBreakPunctuation(token)) score -= 2;

        return score;
    };
    const getCaptionThresholds = (isVerticalLayout: boolean) => ({
        minLength: isVerticalLayout ? 8 : 10,
        targetLength: isVerticalLayout ? 12 : 16,
        maxLength: isVerticalLayout ? 16 : 20,
    });

    const joinCaptionText = (left: string, right: string): string => {
        const lhs = left.trimEnd();
        const rhs = right.trim();
        if (!lhs) return rhs;
        if (!rhs) return lhs;
        if (isBreakPunctuation(rhs[0] || rhs)) return `${lhs}${rhs}`;
        if (requiresNoSpaceJoin(lhs + rhs)) return `${lhs}${rhs}`;
        return `${lhs} ${rhs}`;
    };

    const normalizeCompareToken = (text: string) =>
        String(text || '')
            .normalize('NFKC')
            .replace(/[\s"'`“”‘’.,!?;:…、。！？；：()[\]{}<>《》【】]/gu, '')
            .trim()
            .toLowerCase();

    const getMissingOriginalTail = (originalText: string, renderedText: string): string => {
        const original = String(originalText || '').trim();
        const rendered = String(renderedText || '').trim();
        if (!original || !rendered) return '';

        const originalChars = Array.from(original);
        const normalizedChars: Array<{ char: string; originalIndex: number }> = [];
        originalChars.forEach((char, index) => {
            const normalized = normalizeCompareToken(char);
            if (!normalized) return;
            Array.from(normalized).forEach((normalizedChar) => {
                normalizedChars.push({ char: normalizedChar, originalIndex: index });
            });
        });

        const normalizedOriginal = normalizedChars.map((entry) => entry.char).join('');
        const normalizedRendered = normalizeCompareToken(rendered);
        if (!normalizedOriginal || !normalizedRendered) return '';
        if (!normalizedOriginal.startsWith(normalizedRendered)) return '';
        if (normalizedOriginal.length === normalizedRendered.length) return '';

        const boundary = normalizedChars[normalizedRendered.length - 1];
        return original.slice((boundary?.originalIndex ?? -1) + 1).trim();
    };

    const tokenizeCaptionText = (text: string): string[] => {
        if (typeof Intl !== 'undefined' && typeof Intl.Segmenter !== 'undefined') {
            const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
            const segments = Array.from(segmenter.segment(text), ({ segment }) => segment)
                .filter((segment) => segment.trim().length > 0);

            if (segments.length > 0) {
                return segments;
            }
        }

        if (/\s/.test(text)) {
            return text.split(/\s+/).filter(Boolean);
        }

        return Array.from(text);
    };

    const rebalanceCaptionChunks = (chunks: string[], isVerticalLayout: boolean): string[] => {
        const { minLength, maxLength } = getCaptionThresholds(isVerticalLayout);
        const merged: string[] = [];

        chunks
            .map((chunk) => chunk.trim())
            .filter((chunk) => chunk.length > 0)
            .forEach((chunk) => {
                if (merged.length === 0) {
                    merged.push(chunk);
                    return;
                }

                const previous = merged[merged.length - 1] || '';
                const candidate = joinCaptionText(previous, chunk);
                const shouldMerge = chunk.length < minLength || previous.length < minLength;

                if (shouldMerge && candidate.length <= maxLength + Math.floor(minLength / 2)) {
                    merged[merged.length - 1] = candidate;
                    return;
                }

                merged.push(chunk);
            });

        if (merged.length > 1) {
            const last = merged[merged.length - 1] || '';
            const previous = merged[merged.length - 2] || '';
            const candidate = joinCaptionText(previous, last);
            if (last.length < minLength && candidate.length <= maxLength + Math.floor(minLength / 2)) {
                merged.splice(merged.length - 2, 2, candidate);
            }
        }

        return merged;
    };

    const splitDenseCaption = (text: string, isVerticalLayout: boolean): string[] => {
        const tokens = tokenizeCaptionText(text);
        const chunks: string[] = [];
        const { targetLength, maxLength } = getCaptionThresholds(isVerticalLayout);
        let current = '';

        tokens.forEach((token) => {
            const next = joinCaptionText(current, token);
            if (isBreakPunctuation(token)) {
                current = next;
                if (current.trim()) {
                    chunks.push(current.trim());
                    current = '';
                }
                return;
            }

            if (current && next.length > maxLength) {
                chunks.push(current.trim());
                current = token;
                return;
            }

            current = next;

            if (current.length >= targetLength) {
                chunks.push(current.trim());
                current = '';
            }
        });

        if (current.trim()) {
            chunks.push(current.trim());
        }

        return rebalanceCaptionChunks(
            chunks.filter(chunk => /[\p{L}\p{N}]/u.test(chunk)),
            isVerticalLayout
        );
    };

    const splitCaptionForDisplay = (text: string, isVerticalLayout: boolean): string[] => {
        const normalized = text.replace(/\s+/g, ' ').trim();
        if (!normalized) return [];

        if (containsDenseScript(normalized) && !/\s/.test(normalized)) {
            return splitDenseCaption(normalized, isVerticalLayout);
        }

        const { minLength, targetLength, maxLength } = getCaptionThresholds(isVerticalLayout);
        const words = normalized.split(/\s+/).filter(Boolean);
        if (words.length <= 1) {
            return [normalized];
        }

        const chunks: string[] = [];
        let current = '';

        words.forEach((word, index) => {
            const nextWord = words[index + 1] || null;
            const remainingText = words.slice(index + 1).join(' ').trim();
            const candidate = joinCaptionText(current, word);
            const remainingWithCurrent = joinCaptionText(word, remainingText);
            const wouldCreateTinyTailOnOverflow =
                remainingWithCurrent.length > 0 &&
                (
                    remainingWithCurrent.length < minLength + 4 ||
                    remainingWithCurrent.length < Math.max(8, Math.round(candidate.length * 0.4))
                );

            if (current && candidate.length > maxLength) {
                if (wouldCreateTinyTailOnOverflow && candidate.length <= maxLength + 10) {
                    current = candidate;
                    return;
                }
                chunks.push(current.trim());
                current = word;
                return;
            }

            current = candidate;

            const strongBreak =
                isBreakPunctuation(word) ||
                isKoreanSentenceEnd(word) ||
                (nextWord !== null && startsWithKoreanConnector(nextWord));
            const softBreak =
                current.length >= targetLength &&
                !endsWithWeakKoreanBoundary(word) &&
                !endsWithKoreanConnector(word);
            const wouldCreateTinyTail =
                remainingText.length > 0 &&
                (
                    remainingText.length < minLength ||
                    remainingText.length < Math.max(6, Math.round(candidate.length * 0.3))
                );

            if ((strongBreak || softBreak) && !wouldCreateTinyTail) {
                chunks.push(current.trim());
                current = '';
            }
        });

        if (current.trim()) {
            chunks.push(current.trim());
        }

        return rebalanceCaptionChunks(
            chunks.filter((chunk) => chunk.length > 0),
            isVerticalLayout
        ).flatMap((chunk) => {
            if (chunk.length <= maxLength || chunk.split(/\s+/).length <= 1) {
                return [chunk];
            }

            const innerWords = chunk.split(/\s+/).filter(Boolean);
            const innerChunks: string[] = [];
            let innerCurrent = '';
            innerWords.forEach((innerWord) => {
                const candidate = joinCaptionText(innerCurrent, innerWord);
                if (innerCurrent && candidate.length > Math.max(minLength, targetLength)) {
                    innerChunks.push(innerCurrent.trim());
                    innerCurrent = innerWord;
                    return;
                }
                innerCurrent = candidate;
            });
            if (innerCurrent.trim()) {
                innerChunks.push(innerCurrent.trim());
            }
            return innerChunks;
        });
    };

    const normalizeCaptionTextForCompare = (text: string) =>
        String(text || '')
            .normalize('NFKC')
            .replace(/\s+/g, ' ')
            .trim();

    const buildBalancedCaptionChunks = (text: string, isVerticalLayout: boolean): string[] => {
        const normalized = normalizeCaptionTextForCompare(text);
        if (!normalized) return [];

        const words = normalized.split(/\s+/).filter(Boolean);
        if (words.length <= 1) {
            return splitCaptionForDisplay(normalized, isVerticalLayout);
        }

        const { targetLength, maxLength } = getCaptionThresholds(isVerticalLayout);
        const totalLength = normalized.replace(/\s+/g, '').length;
        const desiredChunkCount = isVerticalLayout && totalLength >= 18
            ? Math.max(2, Math.ceil(totalLength / targetLength))
            : Math.max(1, Math.ceil(totalLength / targetLength));
        if (desiredChunkCount <= 1) {
            return splitCaptionForDisplay(normalized, isVerticalLayout);
        }

        const targetCharsPerChunk = totalLength / desiredChunkCount;
        const chunks: string[] = [];
        let currentWords: string[] = [];
        let currentChars = 0;

        words.forEach((word, index) => {
            const wordChars = word.replace(/\s+/g, '').length;
            const remainingWords = words.length - index;
            const remainingChunks = desiredChunkCount - chunks.length;
            const nextChars = currentChars + wordChars;

            currentWords.push(word);
            currentChars = nextChars;

            const currentText = currentWords.join(' ').trim();
            const atLastWord = index === words.length - 1;
            const shouldForceBreakBySize = currentText.length >= maxLength;
            const shouldBreakNearTarget =
                currentChars >= targetCharsPerChunk &&
                remainingWords >= remainingChunks &&
                currentWords.length > 0;

            if ((shouldForceBreakBySize || shouldBreakNearTarget) && !atLastWord) {
                chunks.push(currentText);
                currentWords = [];
                currentChars = 0;
            }
        });

        if (currentWords.length > 0) {
            chunks.push(currentWords.join(' ').trim());
        }

        if (isVerticalLayout && chunks.length === 1 && words.length > 1 && totalLength >= 18) {
            const midpoint = Math.ceil(words.length / 2);
            const first = words.slice(0, midpoint).join(' ').trim();
            const second = words.slice(midpoint).join(' ').trim();
            return [first, second].filter((chunk) => chunk.length > 0);
        }

        return chunks.filter((chunk) => chunk.length > 0);
    };

    const buildCaptionChunksFromFullText = (text: string, isVerticalLayout: boolean): string[] => {
        const normalized = normalizeCaptionTextForCompare(text);
        if (!normalized) return [];
        const chunks = isVerticalLayout
            ? buildBalancedCaptionChunks(normalized, isVerticalLayout)
            : splitCaptionForDisplay(normalized, isVerticalLayout);
        if (chunks.length !== 2) {
            return chunks;
        }

        let [first, second] = chunks;
        const words = first.split(/\s+/).filter(Boolean);
        const getRatio = () => first.replace(/\s+/g, '').length / Math.max(1, second.replace(/\s+/g, '').length);

        while (words.length > 1 && getRatio() > 3) {
            const moved = words.pop();
            if (!moved) break;
            first = words.join(' ').trim();
            second = joinCaptionText(moved, second);
        }

        return [first, second].filter((chunk) => chunk.trim().length > 0);
    };

    const expandTimedSegmentsForDisplay = (
        segments: Array<{ text: string; start: number; end: number }>,
        isVerticalLayout: boolean
    ): Array<{ text: string; start: number; end: number }> => {
        const expanded: Array<{ text: string; start: number; end: number }> = [];

        segments.forEach((segment) => {
            const chunks = buildCaptionChunksFromFullText(segment.text, isVerticalLayout);
            if (chunks.length <= 1) {
                expanded.push({
                    text: normalizeCaptionTextForCompare(segment.text),
                    start: segment.start,
                    end: segment.end,
                });
                return;
            }

            const duration = Math.max(0.05, segment.end - segment.start);
            const totalChars = Math.max(1, chunks.reduce((sum, chunk) => sum + chunk.length, 0));
            let cursor = segment.start;

            chunks.forEach((chunk, chunkIndex) => {
                const remaining = chunks.length - chunkIndex;
                const remainingDuration = Math.max(0.05, segment.end - cursor);
                const proportional = remaining === 1
                    ? remainingDuration
                    : Math.max(
                        0.12,
                        (duration * chunk.length) / totalChars
                    );
                const chunkDuration = remaining === 1
                    ? remainingDuration
                    : Math.min(remainingDuration - (remaining - 1) * 0.08, proportional);
                const end = chunkIndex === chunks.length - 1
                    ? segment.end
                    : Math.max(cursor + 0.08, cursor + chunkDuration);

                expanded.push({
                    text: chunk,
                    start: cursor,
                    end,
                });
                cursor = end;
            });
        });

        return expanded.filter((segment) => segment.text.trim().length > 0 && segment.end > segment.start);
    };

    const buildFullSceneCaptionSegments = (
        text: string,
        totalFrames: number,
        fpsValue: number,
        isVerticalLayout: boolean
    ): Array<{ text: string; start: number; end: number }> => {
        const normalized = normalizeCaptionTextForCompare(text);
        if (!normalized) return [];

        const chunks = buildCaptionChunksFromFullText(normalized, isVerticalLayout);
        if (chunks.length === 0) return [];

        const durations = buildFallbackCaptionDurations(chunks, Math.max(1, totalFrames));
        const segments: Array<{ text: string; start: number; end: number }> = [];
        let offsetFrames = 0;

        chunks.forEach((chunk, index) => {
            const durationFrames = Math.max(1, durations[index] || 1);
            const start = offsetFrames / fpsValue;
            const end = Math.min(totalFrames, offsetFrames + durationFrames) / fpsValue;
            segments.push({
                text: chunk,
                start,
                end: Math.max(start + (1 / fpsValue), end),
            });
            offsetFrames += durationFrames;
        });

        if (segments.length > 0) {
            segments[segments.length - 1].end = Math.max(
                segments[segments.length - 1].start + (1 / fpsValue),
                totalFrames / fpsValue
            );
        }

        return segments;
    };

    const retimeCaptionSegmentsToSceneAudio = (
        segments: Array<{ text: string; start: number; end: number }>,
        totalFrames: number,
        fpsValue: number
    ): Array<{ text: string; start: number; end: number }> => {
        const validSegments = segments
            .map((segment) => ({
                text: normalizeCaptionTextForCompare(segment.text),
                start: segment.start,
                end: segment.end,
            }))
            .filter((segment) => segment.text.length > 0);

        if (validSegments.length === 0) return [];
        if (validSegments.length === 1) {
            return [{
                text: validSegments[0].text,
                start: 0,
                end: Math.max(1 / fpsValue, totalFrames / fpsValue),
            }];
        }

        const chunks = validSegments.map((segment) => segment.text);
        const durations = buildFallbackCaptionDurations(chunks, Math.max(1, totalFrames));
        const retimed: Array<{ text: string; start: number; end: number }> = [];
        let offsetFrames = 0;

        chunks.forEach((chunk, index) => {
            const durationFrames = Math.max(1, durations[index] || 1);
            const start = offsetFrames / fpsValue;
            const end = Math.min(totalFrames, offsetFrames + durationFrames) / fpsValue;
            retimed.push({
                text: chunk,
                start,
                end: Math.max(start + (1 / fpsValue), end),
            });
            offsetFrames += durationFrames;
        });

        retimed[retimed.length - 1].end = Math.max(
            retimed[retimed.length - 1].start + (1 / fpsValue),
            totalFrames / fpsValue
        );

        return retimed;
    };

    const mergeTinyCaptionSegments = (
        segments: Array<{ text: string; start: number; end: number }>,
        minDurationSec: number
    ): Array<{ text: string; start: number; end: number }> => {
        if (segments.length <= 1) return segments;

        const merged = segments.map((segment) => ({ ...segment }));
        let index = 0;

        while (index < merged.length) {
            const current = merged[index];
            if (!current) break;

            const duration = Math.max(0, current.end - current.start);
            if (duration >= minDurationSec || merged.length === 1) {
                index += 1;
                continue;
            }

            const previous = index > 0 ? merged[index - 1] : null;
            const next = index < merged.length - 1 ? merged[index + 1] : null;

            if (previous) {
                previous.text = joinCaptionText(previous.text, current.text);
                previous.end = Math.max(previous.end, current.end);
                merged.splice(index, 1);
                index = Math.max(0, index - 1);
                continue;
            }

            if (next) {
                next.text = joinCaptionText(current.text, next.text);
                next.start = Math.min(next.start, current.start);
                merged.splice(index, 1);
                continue;
            }

            index += 1;
        }

        return merged;
    };

    const hasPathologicalCaptionTiming = (
        segments: Array<{ text: string; start: number; end: number }>,
        sourceText: string
    ): boolean => {
        if (segments.length < 2) return false;

        const normalizedSource = normalizeCaptionTextForCompare(sourceText);
        const totalChars = Math.max(1, normalizedSource.replace(/\s+/g, '').length);
        const totalDuration = segments.reduce((sum, segment) => sum + Math.max(0, segment.end - segment.start), 0);
        if (totalDuration <= 0) return false;

        return segments.some((segment) => {
            const segmentChars = Math.max(1, normalizeCaptionTextForCompare(segment.text).replace(/\s+/g, '').length);
            const duration = Math.max(0, segment.end - segment.start);
            const charRatio = segmentChars / totalChars;
            const durationRatio = duration / totalDuration;

            return (
                duration < 0.18 ||
                segmentChars >= 4 &&
                charRatio >= 0.12 &&
                durationRatio < Math.max(0.12, charRatio * 0.4)
            );
        });
    };

    const buildTimedCaptionSegments = (
        wordTimestamps: { word: string; start: number; end: number }[],
        isVerticalLayout: boolean
    ): Array<{ text: string; start: number; end: number }> => {
        const normalizedWordTimestamps = wordTimestamps
            .map((wt) => ({
                word: String(wt?.word || '').trim(),
                start: Number(wt?.start) || 0,
                end: Number(wt?.end) || 0,
            }))
            .filter((wt) => wt.word && wt.end > wt.start);

        if (normalizedWordTimestamps.length === 0) return [];

        const anchorStart = normalizedWordTimestamps[0]?.start ?? 0;
        const relativeWordTimestamps = normalizedWordTimestamps.map((wt) => ({
            word: wt.word,
            start: Math.max(0, wt.start - anchorStart),
            end: Math.max(0, wt.end - anchorStart),
        }));
        const { minLength, targetLength, maxLength } = getCaptionThresholds(isVerticalLayout);

        const minSegmentSeconds = isVerticalLayout ? 0.7 : 1.2;
        const targetSegmentSeconds = isVerticalLayout ? 1.1 : 1.9;
        const maxSegmentSeconds = isVerticalLayout ? 2.2 : 3.2;
        const hardGapThreshold = isVerticalLayout ? 0.7 : 0.9;
        const maxCandidateLength = maxLength + 8;
        const n = relativeWordTimestamps.length;

        const joinedCache = new Map<string, string>();
        const getJoinedText = (startIndex: number, endIndex: number) => {
            const key = `${startIndex}:${endIndex}`;
            const cached = joinedCache.get(key);
            if (cached) return cached;
            let text = '';
            for (let i = startIndex; i <= endIndex; i += 1) {
                text = joinCaptionText(text, relativeWordTimestamps[i]?.word || '');
            }
            const trimmed = text.trim();
            joinedCache.set(key, trimmed);
            return trimmed;
        };

        type Candidate = { text: string; start: number; end: number; score: number };
        const candidateCache = new Map<string, Candidate | null>();
        const getCandidate = (startIndex: number, endIndex: number): Candidate | null => {
            const key = `${startIndex}:${endIndex}`;
            if (candidateCache.has(key)) return candidateCache.get(key) ?? null;

            const text = getJoinedText(startIndex, endIndex);
            const start = relativeWordTimestamps[startIndex]?.start ?? 0;
            const end = relativeWordTimestamps[endIndex]?.end ?? start;
            const duration = Math.max(0, end - start);
            const length = text.length;

            if (!text || duration <= 0 || length > maxCandidateLength) {
                candidateCache.set(key, null);
                return null;
            }

            const lastToken = relativeWordTimestamps[endIndex]?.word || '';
            const nextGap = endIndex < n - 1
                ? (relativeWordTimestamps[endIndex + 1].start - relativeWordTimestamps[endIndex].end)
                : null;
            const nextToken = endIndex < n - 1 ? (relativeWordTimestamps[endIndex + 1]?.word || '') : null;

            let score = 0;
            score += Math.abs(length - targetLength) * 1.4;
            score += Math.abs(duration - targetSegmentSeconds) * 9;

            if (length < minLength) score += (minLength - length) * 5;
            if (duration < minSegmentSeconds) score += (minSegmentSeconds - duration) * 18;
            if (duration > maxSegmentSeconds) score += (duration - maxSegmentSeconds) * 12;

            score -= getKoreanBoundaryScore(lastToken, nextToken);
            if (nextGap !== null && nextGap > hardGapThreshold) score -= 10;
            if (endIndex === n - 1) score -= 4;

            candidateCache.set(key, { text, start, end, score });
            return { text, start, end, score };
        };

        const dp = new Array<number>(n + 1).fill(Number.POSITIVE_INFINITY);
        const nextBreak = new Array<number>(n).fill(-1);
        dp[n] = 0;

        for (let i = n - 1; i >= 0; i -= 1) {
            for (let j = i; j < n; j += 1) {
                const candidate = getCandidate(i, j);
                if (!candidate) break;

                const totalScore = candidate.score + dp[j + 1];
                if (totalScore < dp[i]) {
                    dp[i] = totalScore;
                    nextBreak[i] = j + 1;
                }
            }
        }

        const segments: Array<{ text: string; start: number; end: number }> = [];
        let cursor = 0;
        while (cursor < n) {
            const breakIndex = nextBreak[cursor] > cursor ? nextBreak[cursor] : Math.min(n, cursor + 1);
            const candidate = getCandidate(cursor, breakIndex - 1);
            if (!candidate) break;
            segments.push({
                text: candidate.text,
                start: candidate.start,
                end: candidate.end,
            });
            cursor = breakIndex;
        }

        if (segments.length >= 2) {
            const last = segments[segments.length - 1];
            const prev = segments[segments.length - 2];
            const combinedText = joinCaptionText(prev.text, last.text);
            const lastDuration = last.end - last.start;

            if ((last.text.length < minLength || lastDuration < minSegmentSeconds) && combinedText.length <= maxCandidateLength) {
                prev.text = combinedText;
                prev.end = last.end;
                segments.pop();
            }
        }

        return segments.filter((segment) => segment.text.trim().length > 0);
    };

    const buildFallbackCaptionDurations = (chunks: string[], totalFrames: number): number[] => {
        if (chunks.length === 0) return [];
        if (chunks.length === 1) return [Math.max(1, totalFrames)];
        if (chunks.length === 2) {
            const firstChars = Math.max(1, chunks[0]?.replace(/\s+/g, '').length || 0);
            const secondChars = Math.max(1, chunks[1]?.replace(/\s+/g, '').length || 0);
            const totalChars = firstChars + secondChars;
            const firstDuration = Math.max(
                MIN_CAPTION_DISPLAY_FRAMES,
                Math.min(
                    totalFrames - MIN_CAPTION_DISPLAY_FRAMES,
                    Math.round((totalFrames * firstChars) / Math.max(1, totalChars))
                )
            );
            const secondDuration = Math.max(1, totalFrames - firstDuration);
            return [firstDuration, secondDuration];
        }

        const totalChars = Math.max(
            1,
            chunks.reduce((sum, chunk) => sum + chunk.replace(/\s+/g, '').length, 0)
        );
        const durations: number[] = [];
        let remainingFrames = Math.max(1, totalFrames);
        let remainingChars = totalChars;

        chunks.forEach((chunk, index) => {
            const remainingChunks = chunks.length - index;
            if (remainingChunks === 1) {
                durations.push(Math.max(1, remainingFrames));
                return;
            }

            const chunkChars = Math.max(1, chunk.replace(/\s+/g, '').length);
            const proportional = Math.round((remainingFrames * chunkChars) / Math.max(1, remainingChars));
            const reserveForRest = Math.max(1, MIN_CAPTION_DISPLAY_FRAMES) * (remainingChunks - 1);
            const maxForCurrent = Math.max(MIN_CAPTION_DISPLAY_FRAMES, remainingFrames - reserveForRest);
            const duration = Math.max(
                MIN_CAPTION_DISPLAY_FRAMES,
                Math.min(maxForCurrent, proportional)
            );

            durations.push(duration);
            remainingFrames -= duration;
            remainingChars -= chunkChars;
        });

        return durations;
    };

    // Standardize input - be paranoid about where scenes are
    let appState = cenemaData?.data || cenemaData?.input_data || cenemaData || {};
    console.log('Initial appState Keys:', Object.keys(appState));

    // TTS 재생성 메타 세팅
    cenemaProjectMeta.voiceId = appState.voice_id || cenemaData?.voice_id || 'Aoede';
    cenemaProjectMeta.ttsSpeed = appState.audioConfig?.speed || cenemaData?.audioConfig?.speed || 1.2;

    const ratioRaw = (appState.ratio || appState.aspect_ratio || cenemaData.ratio || cenemaData.aspect_ratio || (cenemaData.input_data as any)?.aspect_ratio || "16:9").toString().toLowerCase();
    const isSquare = ratioRaw.includes("1:1") || ratioRaw.includes("square");
    isVertical = isVerticalLayout || isSquare || ratioRaw.includes("9:16") || ratioRaw.includes("shorts") || ratioRaw.includes("vertical");
    console.log('Detected Ratio:', ratioRaw, 'isVertical:', isVertical);
    const targetWidth = isVertical ? 1080 : 1920;
    const targetHeight = isVertical ? 1920 : 1080;

    console.log(`Mapping with Aspect Ratio: ${ratioRaw} (${targetWidth}x${targetHeight})`);

    // Deep fallback if structure is nested data.data
    if (appState.data && !appState.scenes && !appState.script_timeline) {
        console.log('Found nested data property and no scenes/timeline, diving deeper...');
        appState = appState.data;
    }

    // 3. Resolve scenes array
    console.log('Checking for scenes or script_timeline in appState...');
    let scenes = Array.isArray(appState.scenes) ? appState.scenes :
        Array.isArray(appState.script_timeline) ? appState.script_timeline : [];

    if (scenes.length === 0) {
        console.log('Scenes still empty, checking root payload...');
        if (Array.isArray(cenemaData.scenes)) scenes = cenemaData.scenes;
        else if (Array.isArray(cenemaData.script_timeline)) scenes = cenemaData.script_timeline;
    }

    if (scenes.length === 0) {
        console.log('Scenes still empty, recursively hunting nested payload...');
        const huntScenes = (obj: any, depth = 0): any[] => {
            if (!obj || typeof obj !== 'object' || depth > 8) return [];

            if (Array.isArray(obj.scenes) && obj.scenes.length > 0) {
                return obj.scenes;
            }
            if (Array.isArray(obj.script_timeline) && obj.script_timeline.length > 0) {
                return obj.script_timeline;
            }

            const values = Array.isArray(obj) ? obj : Object.values(obj);
            for (const value of values) {
                const found = huntScenes(value, depth + 1);
                if (found.length > 0) return found;
            }
            return [];
        };

        scenes = huntScenes(appState);
        if (scenes.length === 0) {
            scenes = huntScenes(cenemaData);
        }
    }

    console.log('Resolved Scenes Count:', scenes.length);

    // Normalize scenes from script_timeline if needed
    if (scenes.length > 0 && (scenes[0] as any).visual_url) {
        console.log('Detected script_timeline format. Normalizing...');
        scenes = scenes.map((s: any, i: number) => {
            const normalizedDuration = getSceneDurationSec(s);
            const explicitVideoUrl = s.video_url || (s.preview_mode === 'motion' ? s.visual_url : undefined);
            const explicitImageUrl =
                s.preview_mode === 'motion'
                    ? (s.imageUrl || undefined)
                    : ((s.visual_type === 'image' || !s.visual_type) ? s.visual_url : s.imageUrl);
            const normalized = {
                ...s,
                image: explicitImageUrl,
                video: s.visual_type === 'video' || s.preview_mode === 'motion' || explicitVideoUrl ? explicitVideoUrl : undefined,
                duration: normalizedDuration,
                description: s.text || `Scene ${i + 1}`,
                generatedAudioUrl: s.source_audio_url || s.audio_url,
                audioReferenceUrl: s.source_audio_url || s.audio_url,
                audio_duration: Number.isFinite(Number(s.audio_duration)) ? Number(s.audio_duration) : undefined
            };
            return normalized;
        });
    }

    console.log('Final Normalized Scenes Count:', scenes.length);
    if (scenes.length === 0) {
        console.warn('No scenes found! Full appState dump:', appState);
    }

    const items: TimelineItem[] = [];
    const assets: any[] = [];
    const seenAssets = new Set<string>();
    const sceneWindows = new Map<string, { from: number; to: number }>();

    const tracks: TimelineTrack[] = [
        { id: "text-track", name: "Text", order: 0, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "caption-track", name: "Caption", order: 2, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "animation-track", name: "Animation", order: 3, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "video-track", name: "Visuals", order: 4, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "audio-track", name: "Audio", order: 101, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "background-track", name: "Background", order: 100, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
        { id: "sfx-track", name: "SFX", order: 102, height: 32, locked: false, visible: true, muted: false, solo: false, items: [] },
    ];

    let currentFrame = 0;

    const addAsset = (url: string, type: 'video' | 'image' | 'audio') => {
        if (!url || typeof url !== 'string' || seenAssets.has(url)) return;
        seenAssets.add(url);

        const fileName = url.split('/').pop()?.split('?')[0] || `asset-${assets.length + 1}`;
        assets.push({
            id: url,
            fileName,
            mimeType: type === 'video' ? 'video/mp4' : type === 'image' ? (url.includes('.png') ? 'image/png' : 'image/jpeg') : 'audio/mpeg',
            type
        });
    };

    scenes.forEach((scene: any, index: number) => {
        if (!scene) return;

        const uniqueSceneToken = `scene-${index + 1}`;
        const sourceSceneId = String(scene?.id ?? uniqueSceneToken);
        const sourceWidth = toFiniteNumber(
            scene?.source_width ??
            scene?.sourceWidth ??
            scene?.visual_width ??
            scene?.visualWidth ??
            scene?.image_width ??
            scene?.imageWidth ??
            scene?.video_width ??
            scene?.videoWidth ??
            scene?.width
        );
        const sourceHeight = toFiniteNumber(
            scene?.source_height ??
            scene?.sourceHeight ??
            scene?.visual_height ??
            scene?.visualHeight ??
            scene?.image_height ??
            scene?.imageHeight ??
            scene?.video_height ??
            scene?.videoHeight ??
            scene?.height
        );
        const durationInSeconds = getSceneDurationSec(scene);
        const durationInFrames = Math.round(durationInSeconds * fps);
        const visualSrc = scene.video || scene.image;
        const explicitAudioFrames = Number.isFinite(Number(scene?.audio_duration))
            ? Math.max(1, Math.round(Number(scene.audio_duration) * fps))
            : 0;
        const totalFrames = explicitAudioFrames > 0
            ? explicitAudioFrames
            : durationInFrames;

        const audioTimelineStart = currentFrame;
        const sceneTimelineEnd = audioTimelineStart + totalFrames;
        sceneWindows.set(uniqueSceneToken, {
            from: audioTimelineStart,
            to: sceneTimelineEnd,
        });

        if (visualSrc) {
            const type = scene.video ? "video" : "image";
            addAsset(visualSrc, type);

            const item: TimelineItem = {
                id: `${uniqueSceneToken}-visual`,
                type,
                role: "primary_visual",
                trackId: "video-track",
                from: currentFrame,
                durationInFrames: totalFrames, // Fill the entire scene block (No Gaps)
                label: `Scene ${index + 1}`,
                src: visualSrc,
                mediaId: visualSrc,
                sourceStart: 0,
                sourceEnd: totalFrames,
                sourceDuration: totalFrames,
                trimStart: 0,
                trimEnd: 0,
                volume: 0,
                sceneId: uniqueSceneToken,
                sourceSceneId,
                sceneIndex: index,
                sourceWidth: sourceWidth && sourceWidth > 0 ? sourceWidth : undefined,
                sourceHeight: sourceHeight && sourceHeight > 0 ? sourceHeight : undefined,
                transform: {
                    x: 0,
                    y: 0,
                    width: targetWidth,
                    height: targetHeight,
                    rotation: 0,
                    opacity: 1,
                    objectFit: "cover",
                },
            } as any;

            items.push(item);
            tracks.find(t => t.id === "video-track")?.items.push(item);
        }

        // ──────────────────────────────────────────────
        // 🆕 CAPTION RENDERING
        // Canonical rule:
        // - Prefer word_timestamps as the source of truth
        // - Normalize the first spoken word to scene audio start (0)
        // - Only fall back to scene text when timing data does not exist
        // - Do not apply local lead-in / multiplier / drift compensation here
        // ──────────────────────────────────────────────
        const wordTimestampsRaw = (scene as any).word_timestamps as { word: string; start: number; end: number }[] | undefined;
        const timedSegments = wordTimestampsRaw && wordTimestampsRaw.length > 0
            ? buildTimedCaptionSegments(wordTimestampsRaw, isVertical)
            : [];
        const sceneCaptionSourceText = String(scene.description || scene.text || '').replace(/\s+/g, ' ').trim();
        const sourceCaptionText = normalizeCaptionTextForCompare(sceneCaptionSourceText);
        const timedSegmentsText = normalizeCaptionTextForCompare(timedSegments.map((segment) => segment.text).join(' '));
        const shouldUseFullSceneCaption =
            sourceCaptionText.length > 0 &&
            (
                isVertical ||
                timedSegments.length === 0 ||
                !timedSegmentsText ||
                !sourceCaptionText.startsWith(timedSegmentsText) ||
                timedSegmentsText.length < sourceCaptionText.length
            );
        const expandedTimedSegments = expandTimedSegmentsForDisplay(timedSegments, isVertical);
        const shouldFallbackFromPathologicalTiming =
            sourceCaptionText.length > 0 &&
            !shouldUseFullSceneCaption &&
            hasPathologicalCaptionTiming(expandedTimedSegments, sceneCaptionSourceText);
        const captionSegmentsForRenderSource =
            (shouldUseFullSceneCaption || shouldFallbackFromPathologicalTiming)
                ? buildFullSceneCaptionSegments(sceneCaptionSourceText, totalFrames, fps, isVertical)
                : expandedTimedSegments;
        const captionSegmentsForRenderRaw = retimeCaptionSegmentsToSceneAudio(
            captionSegmentsForRenderSource,
            totalFrames,
            fps
        );
        const captionSegmentsForRender = mergeTinyCaptionSegments(
            captionSegmentsForRenderRaw,
            isVertical ? 0.18 : 0.24
        );

        if (captionSegmentsForRender.length > 0) {
            console.log(`[cenema-mapper] 🎯 Scene ${index + 1}: Using canonical caption timing`, {
                source:
                    shouldUseFullSceneCaption
                        ? (isVertical ? 'vertical-full-scene' : 'full-scene-fallback')
                        : shouldFallbackFromPathologicalTiming
                            ? 'balanced-full-scene-fallback'
                            : 'word_timestamps',
            });

            captionSegmentsForRender.forEach((segment, segmentIndex) => {
                const relativeStart = Math.max(0, Math.round(segment.start * fps));
                const relativeEnd = Math.max(relativeStart + 1, Math.round(segment.end * fps));
                const nextSegment = captionSegmentsForRender[segmentIndex + 1];
                const nextRelativeStart = nextSegment
                    ? Math.max(0, Math.round(nextSegment.start * fps))
                    : null;

                const startFrame = Math.min(
                    sceneTimelineEnd - 1,
                    Math.max(audioTimelineStart, audioTimelineStart + relativeStart)
                );
                let endFrame = Math.max(startFrame + 1, audioTimelineStart + relativeEnd);

                if (nextRelativeStart !== null) {
                    endFrame = Math.max(startFrame + 1, audioTimelineStart + nextRelativeStart);
                } else {
                    endFrame = sceneTimelineEnd;
                }

                endFrame = Math.min(sceneTimelineEnd, endFrame);
                if (endFrame < startFrame + 1) endFrame = Math.min(sceneTimelineEnd, startFrame + 1);

                const chunkDuration = Math.max(1, endFrame - startFrame);

                const item: TimelineItem = {
                    id: `${uniqueSceneToken}-caption-${segmentIndex}`,
                    type: "text",
                    role: "caption",
                    trackId: "caption-track",
                    from: startFrame,
                    durationInFrames: chunkDuration,
                    label: "Caption",
                    text: segment.text,
                    sceneId: uniqueSceneToken,
                    sourceSceneId,
                    sceneIndex: index,
                    fontSize: DEFAULT_CAPTION_FONT_SIZE,
                    fontFamily: "Inter",
                    fontWeight: "bold",
                    color: "#ffffff",
                    textAlign: "center",
                    verticalAlign: "middle",
                    lineHeight: 1.2,
                    stroke: { color: "transparent", width: 0 },
                    textShadow: { color: "transparent", blur: 0, offsetX: 0, offsetY: 0 },
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    transform: {
                        x: 0,
                        y: isVertical ? 710 : 450,
                        width: isVertical ? 1080 : 1700,
                        height: 220,
                        rotation: 0,
                        opacity: 1,
                    },
                } as any;

                items.push(item);
                tracks.find(t => t.id === "caption-track")?.items.push(item);
            });

            if (captionSegmentsForRender.length > 0) {
                console.log('[cenema-mapper][SceneTiming]', {
                    sceneIndex: index + 1,
                    audioTimelineStart,
                    totalFrames,
                    durationInSeconds,
                    audioDuration: scene?.audio_duration,
                    firstWordTimestamp: wordTimestampsRaw?.[0],
                    firstCaptionSegment: captionSegmentsForRender[0],
                    lastCaptionSegment: captionSegmentsForRender[captionSegmentsForRender.length - 1],
                });
            }

        } else if (scene.description) {
            // ⬇️ FALLBACK: Character-ratio based distribution (기존 로직 + 글자수 비례 개선)
            const cleanText = scene.description
                .replace(/["'\u201C\u201D\u2018\u2019\u300C\u300D\u300E\u300F()\uFF08\uFF09\[\]\u3010\u3011{}<>\u300A\u300B\u2022\-\u2014_\/\\@#$%^&*+=|`~]+/g, '')
                .replace(/\s+/g, ' ')
                .trim();
            const chunks: string[] = [];

            if (cleanText.length > 8 && !(containsDenseScript(cleanText) && !/\s/.test(cleanText))) {
                const eojeols = cleanText.split(/\s+/);

                const getBreakScore = (word: string, nextWord: string | null): number => {
                    if (nextWord && /^(그리고|그러나|그래서|하지만|그렇지만|또한|그런데|따라서|즉|결국|마침내|드디어|그러면|그러므로|반면|오히려|그래도|물론|심지어|게다가|더구나|더욱이|뿐만|아니라)/.test(nextWord)) return 12;
                    if (/(니다|습니다|에요|예요|거든요|잖아요|는데요|세요|까요|죠|어요|아요|네요|구요|대요|래요|다고|라고|냐고|답니다)$/.test(word)) return 10;
                    if (/(은|는|이|가|을|를|에|의|도|로|으로|에서|까지|부터|에게|한테|와|과|하고|처럼|만큼|보다|같이|대로|밖에|마저|조차|라도|이라도|이나|나)$/.test(word)) return 7;
                    if (/(하는|되는|있는|없는|했던|되었던|인|적인|스러운|다운|하게|되게|하면|되면|있으면|없으면|하고|되고|있고|없고|하며|되며|하여|되어|해서|돼서)$/.test(word)) return 5;
                    return 1;
                };

                let current = "";
                let bestBreak = { score: 0, text: "", endIdx: -1 };
                const fallbackThresholds = getCaptionThresholds(isVertical);
                const MIN_LEN = fallbackThresholds.minLength;
                const TARGET_LEN = fallbackThresholds.targetLength;
                const MAX_LEN = fallbackThresholds.maxLength;

                for (let i = 0; i < eojeols.length; i++) {
                    const word = eojeols[i];
                    const nextWord = i + 1 < eojeols.length ? eojeols[i + 1] : null;
                    current = current ? current + " " + word : word;

                    if (current.length >= MIN_LEN) {
                        const score = getBreakScore(word, nextWord);
                        if (score > bestBreak.score || (score === bestBreak.score && current.length <= TARGET_LEN)) {
                            bestBreak = { score, text: current, endIdx: i };
                        }
                    }

                    if (current.length >= MAX_LEN || (current.length >= TARGET_LEN && bestBreak.score >= 5)) {
                        if (bestBreak.endIdx >= 0) {
                            chunks.push(bestBreak.text);
                            current = eojeols.slice(bestBreak.endIdx + 1, i + 1).join(" ");
                        } else {
                            chunks.push(current);
                            current = "";
                        }
                        bestBreak = { score: 0, text: "", endIdx: -1 };
                    }
                }
                if (current.trim().length > 0) chunks.push(current.trim());

            } else if (cleanText.length > 0) {
                chunks.push(cleanText);
            }

            if (chunks.length === 1 && containsDenseScript(cleanText) && !/\s/.test(cleanText)) {
                chunks.splice(0, chunks.length, ...splitDenseCaption(cleanText, isVertical));
            }

            const validChunks = chunks.filter(c => /[\p{L}\p{N}]/u.test(c));
            if (validChunks.length === 0) { currentFrame += totalFrames; return; }

            // 🆕 글자수 비례 배분 + 최소 표시 시간 + 마지막 캡션 끝까지 보장
            let chunkOffset = 0;
            const fallbackCaptionFrames = Math.max(1, totalFrames);
            const chunkDurations = buildFallbackCaptionDurations(validChunks, fallbackCaptionFrames);

            validChunks.forEach((chunk, chunkIndex) => {
                const chunkDuration = chunkDurations[chunkIndex] || MIN_CAPTION_DISPLAY_FRAMES;
                const startFrame = Math.min(
                    sceneTimelineEnd - 1,
                    audioTimelineStart + chunkOffset
                );
                const maxChunkDuration = Math.max(1, sceneTimelineEnd - startFrame);

                const item: TimelineItem = {
                    id: `${uniqueSceneToken}-fallback-caption-${chunkIndex}`,
                    type: "text",
                    role: "caption",
                    trackId: "caption-track",
                    from: startFrame,
                    durationInFrames: Math.max(1, Math.min(chunkDuration, maxChunkDuration)),
                    label: "Caption",
                    text: chunk,
                    sceneId: uniqueSceneToken,
                    sourceSceneId,
                    sceneIndex: index,
                    fontSize: DEFAULT_CAPTION_FONT_SIZE,
                    fontFamily: "Inter",
                    fontWeight: "bold",
                    color: "#ffffff",
                    textAlign: "center",
                    verticalAlign: "middle",
                    lineHeight: 1.2,
                    stroke: { color: "transparent", width: 0 },
                    textShadow: { color: "transparent", blur: 0, offsetX: 0, offsetY: 0 },
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    transform: {
                        x: 0,
                        y: isVertical ? 710 : 450,
                        width: isVertical ? 1080 : 1700,
                        height: 220,
                        rotation: 0,
                        opacity: 1,
                    },
                } as any;

                items.push(item);
                tracks.find(t => t.id === "caption-track")?.items.push(item);
                chunkOffset += chunkDuration;
            });
        }

        const resolvedGeneratedAudioUrl = scene.generatedAudioUrl || scene.audioReferenceUrl || scene.source_audio_url || scene.audio_url;

        if (resolvedGeneratedAudioUrl) {
            const rawAudioUrl = unwrapProxyUrl(resolvedGeneratedAudioUrl);
            const audioUrl = isPublicR2Url(rawAudioUrl)
                ? rawAudioUrl
                : rawAudioUrl;
            addAsset(audioUrl, 'audio');

            const fallbackAudioFrames = Math.max(1, Math.round(getSceneDurationSec(scene) * fps));
            const combinedSourceStart = 0;
            const combinedSourceEnd = explicitAudioFrames > 0
                ? explicitAudioFrames
                : fallbackAudioFrames;
            const combinedDuration = Math.max(1, combinedSourceEnd - combinedSourceStart);

            const stableId = `${uniqueSceneToken}-voiceover`;

            const item: TimelineItem = {
                id: stableId,
                type: "audio",
                trackId: "audio-track",
                from: audioTimelineStart,
                durationInFrames: combinedDuration,
                label: "Voiceover",
                src: audioUrl,
                mediaId: audioUrl,
                volume: 1,
                sourceStart: combinedSourceStart,
                sourceDuration: Math.max(explicitAudioFrames, combinedSourceEnd),
                trimStart: 0,
                trimEnd: 0,
                sceneText: scene.description || scene.text || '',
                sceneId: uniqueSceneToken,
                sourceSceneId,
                sceneIndex: index,
            } as any;

            items.push(item);
            tracks.find(t => t.id === "audio-track")?.items.push(item);
        }

        // Advance global frame counter by the exact total block size
        currentFrame += totalFrames;
    });

    console.log('Final Mapped Items:', items.length);
    console.groupEnd();

    // Generate stable per-clip motion so repeated scenes don't all drift the same way.
    const keyframes: any[] = [];

    items.forEach(item => {
        // Exclude background tracks from animation
        const role = (item as any).role;
        const isBackground = role === 'background' || item.trackId === 'background-track' || (item.trackId && item.trackId.includes('background'));

        if (item.type === 'image' && !isBackground) {
            const motionEntry = buildClipMotionEntry(item, fps);
            if (motionEntry) {
                keyframes.push(motionEntry);
            }
        }
    });

    const captionTrack = tracks.find((track) => track.id === "caption-track");
    if (captionTrack && captionTrack.items.length > 1) {
        captionTrack.items = normalizeCaptionTrackItems(captionTrack.items as TimelineItem[], sceneWindows);
        const normalizedById = new Map(captionTrack.items.map((item) => [item.id, item]));
        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            if (!item) continue;
            const normalized = normalizedById.get(item.id);
            if (normalized) {
                items[i] = normalized;
            }
        }
    }

    // Only return tracks that have items (avoids empty gap rows in timeline)
    const populatedTracks = tracks.filter(t => t.items.length > 0);
    const finalTracks = populatedTracks.length > 0 ? populatedTracks : tracks.slice(0, 1);

    return { items, tracks: finalTracks, assets, keyframes };
};
